import { useEffect, useLayoutEffect } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { notification } from 'antd';

import { emitSessionExpired, onSessionExpired } from '@/shared/lib/events/sessionExpired';
import { useSessionCheck } from '@/shared/lib/hooks/useSessionCheck';
import { LoadingState } from '@/shared/ui/states/LoadingState';

import { useAuthStore } from '@/entities/auth';

import { readPersistedDemoUser } from '@/features/auth/lib/demoAuth';
import { redirectToSSOLogin } from '@/features/auth/lib/ssoRedirect';
import { useCurrentUser } from '@/features/auth/model';

type AuthProviderProps = {
  children: React.ReactNode;
};

const IS_DEMO_MODE = import.meta.env.MODE === 'demo';

/**
 * 사용자 인증 상태를 확인하고 설정합니다.
 * - 로그아웃 콜백(logout=success) 처리
 * - 이미 사용자 정보가 있으면 API 호출 건너뛰기
 * - 화면 전환 시 세션 체크 (OIDC 보호 안 된 화면 대응)
 * - 세션 만료 이벤트 구독하여 Keycloak 리다이렉트 처리
 * - 데모: 세션 저장소에 프로필이 있을 때만 복구 (자동 익명 로그인 없음)
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const queryClient = useQueryClient();
  const { user: storeUser, setUser, clearAuth } = useAuthStore();

  useSessionCheck();

  useLayoutEffect(() => {
    if (!IS_DEMO_MODE) return;
    if (useAuthStore.getState().user) return;

    const restored = readPersistedDemoUser();
    if (restored) {
      setUser(restored);
    }
  }, [setUser]);

  const urlParams = new URLSearchParams(window.location.search);
  const isLogoutCallback = urlParams.get('logout') === 'success';

  const shouldFetchUser = !IS_DEMO_MODE && !isLogoutCallback && !storeUser;

  const { data: user, isSuccess, isError, isLoading } = useCurrentUser(shouldFetchUser);

  useEffect(() => {
    if (IS_DEMO_MODE) {
      return;
    }

    const unsubscribe = onSessionExpired(() => {
      console.warn('[AuthProvider] 🔔 STEP 5: 세션 만료 이벤트 수신');

      notification.warning({
        key: 'session-expired',
        message: '세션 재인증',
        description: '세션을 다시 확인하고 있습니다. 잠시만 기다려주세요.',
        placement: 'bottomLeft',
        duration: 2,
      });

      setTimeout(() => {
        console.warn('[AuthProvider] 🧹 STEP 6: 인증 상태 클리어');
        clearAuth();
        console.warn('[AuthProvider] 🧹 STEP 7: React Query 캐시 클리어');
        queryClient.clear();
        console.warn('[AuthProvider] 🚀 STEP 8: Keycloak 리다이렉션 호출');
        redirectToSSOLogin();
      }, 1500);
    });

    return unsubscribe;
  }, [clearAuth, queryClient]);

  useEffect(() => {
    if (IS_DEMO_MODE) {
      return;
    }

    if (isLogoutCallback) {
      clearAuth();
      queryClient.clear();
      window.location.href = '/logout';
      return;
    }

    if (storeUser) return;

    if (isSuccess && user) {
      setUser(user);
    } else if (isError) {
      console.warn('[AuthProvider] 인증 실패 - 세션 만료 이벤트 발생');
      if (!window.location.pathname.startsWith('/callback')) {
        emitSessionExpired();
      }
    }
  }, [isLogoutCallback, isSuccess, isError, user, storeUser, setUser, clearAuth, queryClient]);

  if (!IS_DEMO_MODE && isSuccess && user && !storeUser) {
    return <LoadingState message="사용자 정보 동기화 중..." />;
  }

  if (!IS_DEMO_MODE && !isLogoutCallback && isLoading) {
    return <LoadingState message="인증 확인 중..." />;
  }

  return <>{children}</>;
};
